import BaseApiService from './BaseApiService'

export type ESignRequestStatus = 'pending' | 'completed' | 'declined' | 'voided' | 'expired'
export type ESignSignerStatus = 'waiting' | 'pending' | 'signed' | 'declined'
export type ESignFieldKind = 'signature' | 'initials' | 'date' | 'name'

export interface ESignSignerInput {
  email: string
  name: string
  order?: number
}

export interface ESignFieldGeometry {
  kind: ESignFieldKind
  /** 0-based page index. */
  page: number
  /** PDF points, origin at the top-left of the page. */
  x: number
  y: number
  width: number
  height: number
}

export interface ESignFieldInput extends ESignFieldGeometry {
  /** Index into the request's signers array. */
  signerIndex: number
}

export interface ESignCreateRequest {
  renderJobId: string
  title?: string
  message?: string
  signers: ESignSignerInput[]
  sequential?: boolean
  expiresInDays?: number
  reminderIntervalDays?: number
  fields?: ESignFieldInput[]
}

export interface ESignRequestSummary {
  esignRequestId: string
  renderJobId: string
  title: string | null
  status: ESignRequestStatus
  createdAt: string
  completedAt: string | null
  expiresAt: string | null
  signerCount: number
  signedCount: number
}

export interface ESignSigner {
  esignSignerId: string
  email: string
  name: string | null
  order: number | null
  status: ESignSignerStatus
  signedAt: string | null
  viewedAt: string | null
  declinedAt: string | null
  declineReason: string | null
  lastRemindedAt: string | null
}

export interface ESignField extends ESignFieldGeometry {
  fieldId: string
  esignSignerId: string
}

export interface ESignEvent {
  eventType: string
  signerEmail: string | null
  at: string
  ipAddress: string | null
}

export interface ESignStatus {
  esignRequestId: string
  renderJobId: string
  status: ESignRequestStatus
  title: string | null
  message: string | null
  createdAt: string
  completedAt: string | null
  voidedAt: string | null
  declinedAt: string | null
  expiresAt: string | null
  sequential: boolean
  signedDocumentAvailable: boolean
  signers: ESignSigner[]
  fields: ESignField[]
  events: ESignEvent[]
}

export interface ESignSignerField extends ESignFieldGeometry {
  fieldId: string
}

export interface ESignSession {
  title: string | null
  message: string | null
  requesterName: string | null
  signer: { name: string | null; email: string; status: ESignSignerStatus }
  request: { status: ESignRequestStatus; expiresAt: string | null; sequential: boolean }
  yourTurn: boolean
  fields: ESignSignerField[]
  disclosure: string
  signedDocumentAvailable: boolean
}

export interface ESignSignRequest {
  token: string
  consent: true
  signatureText: string
  /** Base64 PNG without the data: prefix. */
  signatureImagePng: string
  initialsImagePng?: string
}

class ESignApiService extends BaseApiService {
  create(req: ESignCreateRequest): Promise<{ esignRequestId: string }> {
    return this.post('/v1/esign/create', req)
  }

  list(status?: ESignRequestStatus): Promise<ESignRequestSummary[]> {
    return this.post('/v1/esign/list', { status })
  }

  status(esignRequestId: string): Promise<ESignStatus> {
    return this.post('/v1/esign/status', { esignRequestId })
  }

  remind(esignRequestId: string): Promise<{ reminded: number }> {
    return this.post('/v1/esign/remind', { esignRequestId })
  }

  void_(esignRequestId: string, reason?: string): Promise<{ voided: boolean }> {
    return this.post('/v1/esign/void', { esignRequestId, reason })
  }

  download(esignRequestId: string): Promise<Blob> {
    return this.postBlob('/v1/esign/download', { esignRequestId })
  }

  session(token: string): Promise<ESignSession> {
    return this.post('/v1/esign/session', { token })
  }

  document(token: string): Promise<Blob> {
    return this.postBlob('/v1/esign/document', { token })
  }

  sign(req: ESignSignRequest): Promise<{ status: 'signed'; requestStatus: ESignRequestStatus; signedAt: string }> {
    return this.post('/v1/esign/sign', req)
  }

  decline(token: string, reason: string): Promise<{ status: 'declined' }> {
    return this.post('/v1/esign/decline', { token, reason })
  }

  signedDocument(token: string): Promise<Blob> {
    return this.postBlob('/v1/esign/signed-document', { token })
  }
}

export const esignApiService = new ESignApiService()
